import { of } from 'rxjs';
import { CurrencyPipe } from '@angular/common';
import {
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  OnChanges,
  OnDestroy,
  OnInit,
  SimpleChanges,
  ViewChild,
  inject,
} from '@angular/core';
import { FormBuilder, Validators } from '@angular/forms';
import { ActivatedRoute, QueryParamsHandling, Router } from '@angular/router';
import { Permissions } from '@app/core/guard/Permissions';
import { NdfOperationFilterRequest } from '@app/core/models/operation/ndf-operation-filter-request';
import { OperationSettleRequest } from '@app/core/models/operation/operation-settle-request';
import { VaultBaseComponent } from '@app/shared/components/base/vault-base.component';
import { SortEvent, TableAction } from '@app/shared/components/table/table.component';
import { AppState } from '@app/state';
import { selectCompanyId, selectDateFormat } from '@app/state/auth/auth.selectors';
import { selectPageFilterCompanyIds } from '@src/app/state/global/global.selectors';
import { CompanyService } from '@services/http/company.service';
import { CompanyDataService } from '@services/data/company/company-data.service';
import { DerivativeCardInfo } from '@interfaces/a-organizar/derivativeCardInfo';
import { DerivativeSummary } from '@interfaces/a-organizar/derivativeSummary';
import {
  NdfDemandsCalculationsFiltered,
  NdfDemandsCalculationsOperation,
} from '@interfaces/a-organizar/ndfDemandsCalculationsFiltered';
import { InstallmentSummary } from '@interfaces/a-organizar/ndfDemandsCalculationsFilteredSummary';
import { NdfOperationFilteredSummary } from '@interfaces/a-organizar/ndfOperationsFilteredSummary';
import { NdfOperationsFilteredSummaryParams } from '@interfaces/a-organizar/ndfOperationsFilteredSummaryParams';
import { PTaxQuotation } from '@interfaces/a-organizar/pTaxQuotation';
import { StatusCount } from '@interfaces/a-organizar/statusCounts';
import { TabViewItem } from '@interfaces/a-organizar/tabViewItem';
import { ApiResponse } from '@interfaces/common/apiResponse';
import { Option } from '@interfaces/common/option';
import { Pagination, TablePagination } from '@interfaces/common/pagination';
import { Store } from '@ngrx/store';
import { TranslateService } from '@ngx-translate/core';
import { DerivativesCardInfoDataService } from '@services/data/derivatives-card-info/derivatives-card-info-data.service';
import { DerivativesSummaryDataService } from '@services/data/derivatives-summary/derivatives-summary-data.service';
import { DateFormatterService } from '@services/helpers/date-formatter.service';
import { ConfirmDialogService } from '@services/helpers/dialog.service';
import { ToastHelperService } from '@services/helpers/toast-helper.service';
import { UtilsService } from '@services/helpers/utils.service';
import { NdfCalculatorService } from '@services/http/ndf-calculator.service';
import { IdValue } from '@src/app/core/interfaces/a-organizar/idValue';
import { DerivativeMtmHttpService } from '@src/app/core/services/http/derivatives/mtm/derivatives-mtm.service';
import { nameof } from '@src/app/core/utils/nameof.utils';
import ModalComexOperationDetailsComponent from '@src/app/features/dashboard/comex/pages/modal-comex-operation-details/modal-comex-operation-details.component';
import { ModalMtmDailyComponent } from '@src/app/features/dashboard/control/derivative-management/derivatives/components/modal-mtm-daily/modal-mtm-daily.component';
import {
  CommitteePrefillOptions,
  ModalCreateCommitteeComponent,
} from '@src/app/features/dashboard/control/risk-management/monitoring/pages/committee/components/modal-create-committee/modal-create-committee.component';
import {
  ModalSaveReversalConfirmationComponent,
  SaveReversalConfirmationData,
} from './modal-save-reversal-confirmation/modal-save-reversal-confirmation.component';
import {
  ConfirmLiquidationData,
  ModalConfirmLiquidationComponent,
} from './modal-confirm-liquidation/modal-confirm-liquidation.component';
import { ModalLiquidateOperationComponent } from './modal-liquidate-operation/modal-liquidate-operation.component';

import { ModalLoanDetailsComponent } from '@src/app/features/dashboard/costs/debt-management/pages/modal-loan-details/modal-loan-details.component';
import { DialogComponent } from '@src/app/shared/components/dialog/dialog.component';
import { ModalConfirmComponent } from '@src/app/shared/components/modal/modal-confirm/modal-confrm.component';
import { ModalDerivativeAttachmentsComponent } from '@src/app/shared/components/modal/modal-derivative-attachments/modal-derivative-attachments.component';
import { ActiveFilterConfig } from '@src/app/shared/interfaces/active-filter.interface';
import { ColumnDefinition } from '@src/app/shared/interfaces/column-definition.interface';
import { MenuItem } from 'primeng/api';
import { DialogService } from 'primeng/dynamicdialog';
import { Paginator, PaginatorState } from 'primeng/paginator';
import {
  Observable,
  Subject,
  debounceTime,
  distinctUntilChanged,
  first,
  skip,
  take,
  takeUntil,
} from 'rxjs';
import { NdfOperationActionsMixin, OperationAction } from './mixins/ndf-operation-actions.mixin';
import { CardOperationSummaryAction } from './ndf-operation-card-summary/ndf-operation-card-summary.component';

import { CompanySelectionService } from '@services/helpers/company-selection.service';
export interface OperationCount {
  all: number;
  simulated: number;
  quoted: number;
  hired: number;
}

import { DerivativeActionPermissionsService } from '@src/app/features/dashboard/control/derivative-management/derivatives/services/derivative-action-permissions.service';

interface EarlyReversalRequestPayload {
  operationId: string;
  settlementDate: string;
  settlementNotional: number;
  settlementRate: number;
  settlementSpot: number;
}

@Component({
  selector: 'app-ndf-operation-list',
  standalone: false,
  templateUrl: './ndf-operation-list.component.html',
  styleUrls: ['./ndf-operation-list.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class NdfOperationListComponent
  extends VaultBaseComponent
  implements OnInit, OnDestroy, OnChanges
{
  /** Dono unico da selecao de empresas; monta `companyId` (`all`, CSV curto ou nada acima do limite), card 3571. */
  private readonly companySelection = inject(CompanySelectionService);
  /** Permissões das ações de gravação (card 3902), as mesmas da Gestão de Derivativos v2. */
  private readonly derivativeActionPermissions = inject(DerivativeActionPermissionsService);

  @ViewChild('paginator', { static: false })
  paginator!: Paginator;

  // ViewChild para as modais
  @ViewChild('liquidateOperationModal')
  liquidateOperationModalRef: ModalLiquidateOperationComponent | null = null;
  @ViewChild('hireOperationDialog') hireOperationDialogRef: DialogComponent | null = null;
  @ViewChild('quoteOperationDialog') quoteOperationDialogRef: DialogComponent | null = null;
  @ViewChild('removeOperationDialog') removeOperationDialogRef: DialogComponent | null = null;
  @ViewChild('revertLiquidationDialog') revertLiquidationDialogRef: DialogComponent | null = null;
  @ViewChild('attachmentsModal') attachmentsModalRef: ModalDerivativeAttachmentsComponent | null =
    null;
  @ViewChild('comexDetailsModal')
  comexDetailsModalRef: ModalComexOperationDetailsComponent | null = null;
  @ViewChild('loanDetailsModal') loanDetailsModalRef: ModalLoanDetailsComponent | null = null;

  @ViewChild('mtmDailyModal') mtmDailyModal!: ModalMtmDailyComponent;
  @ViewChild('saveReversalConfirmationModal')
  saveReversalConfirmationModalRef: ModalSaveReversalConfirmationComponent | null = null;
  @ViewChild('confirmLiquidationModal')
  confirmLiquidationModalRef: ModalConfirmLiquidationComponent | null = null;
  @ViewChild('createCommitteeModal')
  createCommitteeModalRef: ModalCreateCommitteeComponent | null = null;

  breadcrumbs: MenuItem[] = [];
  home: MenuItem = {
    icon: 'pi pi-home',
    routerLink: '/dashboard/dashboard-management',
    label: this.t(this.tk.General.Dashboard),
  };
  operationPermissions = Permissions.DerivativeCalculator;
  companyOptions: Option[] = [];
  selectedCompanyIds: string[] = [];
  isMobile: boolean = false;
  tabViewItems: TabViewItem[] = [];
  tabViewActiveIndex = 0;
  search: string = '';
  activeFilter: boolean = false;

  ndfOperations: NdfOperationFilteredSummary[] = [];
  ndfOperationFilter: NdfOperationsFilteredSummaryParams = {};
  currencyOptions: Option[] = [];
  currentDate: Date = new Date();

  // Novos filtros ativos
  currentFilters: NdfOperationFilterRequest | null = null;
  activeFilterConfigs: ActiveFilterConfig[] = [];
  dateTimeQuoted: string = '';
  dateTimeHired: string = '';
  dateTimeSale: string = '';
  settlementNotional: number = 0;
  settlementRate: number = 0;
  settlementSpot: number = 0;
  settlementDi: number = 0;
  operationMaxValue: number = 100000;
  operationStatus: {
    Simulated: string;
    Quoted: string;
    Hired: string;
    Reversed: string;
  } = {
    Simulated: 'Simulated',
    Quoted: 'Quoted',
    Hired: 'Hired',
    Reversed: 'Reversed',
  };
  liquidationType: 'full' | 'partial' | 'partial-before' | '' = '';
  quoteSuccessfullyContractedLabel: string = '';
  loading: boolean = false;
  private simulateSaleOperationSubject = new Subject<void>();
  private destroy$ = new Subject<void>();
  private pendingEarlyReversalPayload: EarlyReversalRequestPayload | null = null;
  operationQuoteHireProps = nameof<{
    data: string;
    bankOperatorName: string;
  }>();
  operationQuoteForm = this.formBuilder.group({
    [this.operationQuoteHireProps.data]: ['', Validators.required],
    [this.operationQuoteHireProps.bankOperatorName]: [''],
  });
  operationHireForm = this.formBuilder.group({
    [this.operationQuoteHireProps.data]: ['', Validators.required],
    [this.operationQuoteHireProps.bankOperatorName]: [''],
  });

  firstRow: number = 0;
  rowsPerPageOptions: number[] = [10, 20, 50, 100];
  paginationRequest: TablePagination = {
    Page: 0,
    PageSize: 10,
    SortBy: '',
    SortDirection: '',
  };
  pagination: Pagination = {
    total: 0,
    currentPage: 0,
    pages: 0,
    itemsPerPage: 0,
  };

  ptaxQuotesDates: PTaxQuotation[] | null = null;
  operationToHire: NdfDemandsCalculationsOperation | null = null;
  operationToHireSummary: NdfOperationFilteredSummary | null = null;
  operationToSale: NdfDemandsCalculationsOperation | null = null;
  operationToSaleSummary: NdfOperationFilteredSummary | null = null;
  derivativeSummary: DerivativeSummary | null = null;
  derivativeCardInfo: DerivativeCardInfo | null = null;
  ptaxQuotationDate: string | null = null;
  newDemand: NdfDemandsCalculationsFiltered | null = null;
  operationSaleProps = nameof<OperationSettleRequest>();
  operationSaleForm = this.formBuilder.group({
    [this.operationSaleProps.settlementDate]: ['', [Validators.required]],
    [this.operationSaleProps.settlementNotional]: ['', [Validators.required, Validators.min(0)]],
    [this.operationSaleProps.settlementRate]: ['', [Validators.required, Validators.min(0)]],
    [this.operationSaleProps.settlementSpot]: ['0'],
    [this.operationSaleProps.totalAmount]: ['0'],
    [this.operationSaleProps.totalAmountRounded]: ['0'],
    [this.operationSaleProps.netAmount]: ['0'],
    [this.operationSaleProps.netAmountdRounded]: ['0'],
    [this.operationSaleProps.incomeTaxPayable]: ['0'],
    [this.operationSaleProps.incomeTaxPayableRounded]: ['0'],
    [this.operationSaleProps.withholdingTax]: ['0'],
    [this.operationSaleProps.withholdingTaxRounded]: ['0'],
    [this.operationSaleProps.settlementDi]: [0],
  });
  dateFormat: string = 'dd/MM/yyyy';

  // Propriedades para alternar entre visualizações
  viewMode: 'cards' | 'table' = 'cards';

  // Mixin para ações do menu
  private actionsMixin!: NdfOperationActionsMixin;

  // Definições de colunas para o app-table
  columnDefinitions: ColumnDefinition[] = [
    new ColumnDefinition({
      field: 'sequentialId',
      header: 'ID',
      group: 'Identificação',
      sortable: true,
      sortField: 'sequentialId',
      style: { 'min-width': '100px' },
    }),
    new ColumnDefinition({
      field: 'company',
      header: this.t(this.tk.Management.Company),
      group: 'Identificação',
      sortable: true,
      sortField: 'derivativeOperationDemand.company.displayName',
      style: { 'min-width': '150px' },
    }),
    new ColumnDefinition({
      field: 'reference',
      header: this.t(this.tk.Management.NdfReference),
      group: 'Identificação',
      sortable: true,
      sortField: 'reference',
      style: { 'min-width': '150px' },
    }),
    new ColumnDefinition({
      field: 'operationType',
      header: 'Modalidade',
      group: 'Identificação',
      sortable: true,
      sortField: 'operationStrategyType',
      style: { 'min-width': '120px' },
    }),
    new ColumnDefinition({
      field: 'bank',
      header: this.t(this.tk.Management.FinancialInstitution),
      group: 'Identificação',
      sortable: true,
      sortField: 'financialInstitution.displayName',
      style: { 'min-width': '150px' },
      template: 'bank',
    }),
    new ColumnDefinition({
      field: 'linkedInstallments',
      header: 'Parcelas Vinculadas',
      group: 'Identificação',
      sortable: false,
      style: { 'min-width': '200px' },
      template: 'linkedInstallments',
      selected: false,
    }),
    // Grupo Demanda
    new ColumnDefinition({
      field: 'demandSequentialId',
      header: 'Id',
      group: 'Demanda',
      sortable: true,
      sortField: 'demandSequentialId',
      style: { 'min-width': '120px' },
      template: 'demandSequentialId',
    }),
    new ColumnDefinition({
      field: 'demandReference',
      header: 'Referência',
      group: 'Demanda',
      sortable: true,
      sortField: 'derivativeOperationDemand.reference',
      style: { 'min-width': '150px' },
      template: 'demandReference',
    }),
    new ColumnDefinition({
      field: 'demandCurrency',
      header: 'Moeda',
      group: 'Demanda',
      sortable: true,
      sortField: 'derivativeOperationDemand.internationalCurrency.currencyCode',
      style: { 'min-width': '100px' },
      template: 'demandCurrency',
    }),
    new ColumnDefinition({
      field: 'relatedOperations',
      header: 'Operações Relacionadas',
      group: 'Demanda',
      sortable: false,
      style: { 'min-width': '160px' },
      template: 'relatedOperations',
      selected: false,
    }),
    new ColumnDefinition({
      field: 'notionalAmount',
      header: this.t(this.tk.Management.NdfValue),
      group: 'Financeiro',
      sortable: true,
      sortField: 'notionalAmount',
      style: { 'min-width': '150px' },
      template: 'notionalAmount',
    }),
    new ColumnDefinition({
      field: 'forwardRate',
      header: this.t(this.tk.Management.NdfForward),
      group: 'Financeiro',
      sortable: true,
      sortField: 'forwardRate',
      style: { 'min-width': '120px' },
      template: 'forwardRate',
    }),
    new ColumnDefinition({
      field: 'capValue',
      header: this.t(this.tk.Management.NdfCap),
      group: 'Financeiro',
      sortable: true,
      sortField: 'capValue',
      style: { 'min-width': '120px' },
      template: 'capValue',
    }),
    new ColumnDefinition({
      field: 'premium',
      header: 'Prêmio',
      group: 'Financeiro',
      sortable: true,
      sortField: 'premium',
      style: { 'min-width': '120px' },
      template: 'premium',
      selected: true,
    }),
    new ColumnDefinition({
      field: 'strikeBuy',
      header: 'Strike de Compra',
      group: 'Financeiro',
      sortable: true,
      sortField: 'strikeBuy',
      style: { 'min-width': '140px' },
      template: 'strikeBuy',
      selected: true,
    }),
    new ColumnDefinition({
      field: 'strikeSell',
      header: 'Strike de Venda',
      group: 'Financeiro',
      sortable: true,
      sortField: 'strikeSell',
      style: { 'min-width': '140px' },
      template: 'strikeSell',
      selected: true,
    }),
    new ColumnDefinition({
      field: 'referenceExchangeRate',
      header: this.t('Câmbio de referência'),
      group: 'Financeiro',
      sortable: true,
      sortField: 'referenceExchangeRate',
      format: 'currency:4',
      style: { 'min-width': '120px' },
    }),
    new ColumnDefinition({
      field: 'activeIndexer',
      header: 'Indexador Ativo',
      group: 'Financeiro',
      sortable: true,
      sortField: 'activeIndexer',
      style: { 'min-width': '150px' },
      template: 'activeIndexer',
      selected: false,
    }),
    new ColumnDefinition({
      field: 'passiveIndexer',
      header: 'Indexador Passivo',
      group: 'Financeiro',
      sortable: true,
      sortField: 'passiveIndexer',
      style: { 'min-width': '160px' },
      template: 'passiveIndexer',
      selected: false,
    }),
    new ColumnDefinition({
      field: 'activeFixedRatePerYear',
      header: 'Taxa Fixa Ativo a.a.%',
      group: 'Financeiro',
      sortable: true,
      sortField: 'activeFixedRatePerYear',
      style: { 'min-width': '180px' },
      template: 'activeFixedRatePerYear',
      selected: false,
    }),
    new ColumnDefinition({
      field: 'passiveFixedRatePerYear',
      header: 'Taxa Fixa Passivo a.a.%',
      group: 'Financeiro',
      sortable: true,
      sortField: 'passiveFixedRatePerYear',
      style: { 'min-width': '190px' },
      template: 'passiveFixedRatePerYear',
      selected: false,
    }),
    new ColumnDefinition({
      field: 'dayCountConvention',
      header: 'Convenção de Contagem de Dias',
      group: 'Financeiro',
      sortable: true,
      sortField: 'dayCountConvention',
      style: { 'min-width': '220px' },
      template: 'dayCountConvention',
      selected: false,
    }),
    new ColumnDefinition({
      field: 'dueAt',
      header: this.t(this.tk.Management.NdfDueDate),
      group: 'Datas',
      sortable: true,
      sortField: 'dueAt',
      format: 'date',
      style: { 'min-width': '120px' },
    }),
    new ColumnDefinition({
      field: 'operationDate',
      header: this.t(this.tk.Management.NdfDateOfOperation),
      group: 'Datas',
      sortable: true,
      sortField: 'operationDate',
      format: 'date',
      style: { 'min-width': '120px' },
    }),
    new ColumnDefinition({
      field: 'daysCount',
      header: this.t(this.tk.Management.NdfCalendarDays),
      group: 'Datas',
      sortable: true,
      sortField: 'daysCount',
      style: { 'min-width': '100px' },
    }),
    // Grupo Liquidação
    new ColumnDefinition({
      field: 'settlementDate',
      header: 'Data da Liquidação',
      group: 'Liquidação',
      sortable: true,
      sortField: 'settlementDate',
      format: 'date',
      style: { 'min-width': '130px' },
      selected: false,
    }),
    new ColumnDefinition({
      field: 'settlementNotional',
      header: 'Notional Liquidação',
      group: 'Liquidação',
      sortable: true,
      sortField: 'settlementNotional',
      style: { 'min-width': '150px' },
      template: 'settlementNotional',
      selected: false,
    }),
    new ColumnDefinition({
      field: 'settlementRate',
      header: 'Taxa de Liquidação',
      group: 'Liquidação',
      sortable: true,
      sortField: 'settlementRate',
      style: { 'min-width': '140px' },
      template: 'settlementRate',
      selected: false,
    }),
    new ColumnDefinition({
      field: 'totalAmount',
      header: 'Ajuste Bruto',
      group: 'Liquidação',
      sortable: true,
      sortField: 'totalAmount',
      style: { 'min-width': '120px' },
      template: 'totalAmount',
      selected: false,
    }),
    new ColumnDefinition({
      field: 'withholdingTax',
      header: 'IRRF',
      group: 'Liquidação',
      sortable: true,
      sortField: 'withholdingTax',
      style: { 'min-width': '100px' },
      template: 'withholdingTax',
      selected: false,
    }),
    new ColumnDefinition({
      field: 'incomeTaxPayable',
      header: 'IR a Pagar',
      group: 'Liquidação',
      sortable: true,
      sortField: 'incomeTaxPayable',
      style: { 'min-width': '100px' },
      template: 'incomeTaxPayable',
      selected: false,
    }),
    new ColumnDefinition({
      field: 'netAmount',
      header: 'Ajuste Líquido',
      group: 'Liquidação',
      sortable: true,
      sortField: 'netAmount',
      style: { 'min-width': '120px' },
      template: 'netAmount',
      selected: false,
    }),
    new ColumnDefinition({
      selected: false,
      field: 'userName',
      header: this.t('Criado por'),
      group: 'Auditoria',
      sortable: true,
      sortField: 'userName',
      style: { 'min-width': '120px' },
    }),
  ];

  constructor(
    translateService: TranslateService,
    private ndfCalculatorService: NdfCalculatorService,
    private confirmDialogService: ConfirmDialogService,
    private dialogService: DialogService,
    private toast: ToastHelperService,
    private utilsService: UtilsService,
    store: Store<AppState>,
    private formBuilder: FormBuilder,
    private dateFormatter: DateFormatterService,
    private router: Router,
    private route: ActivatedRoute,
    private currencyPipe: CurrencyPipe,
    private derivativesSummaryDataService: DerivativesSummaryDataService,
    private derivativesCardInfoDataService: DerivativesCardInfoDataService,
    private changeDetectorRef: ChangeDetectorRef,
    private derivativeMtmHttp: DerivativeMtmHttpService,
    private companyService: CompanyService,
    private companyDataService: CompanyDataService
  ) {
    super(translateService, store);

    // Inicializar o mixin de ações com função de tradução simplificada
    this.actionsMixin = new NdfOperationActionsMixin(
      (key: string) => this.translateKey(key),
      (action: OperationAction) => this.handleActionFromMixin(action),
      (item: NdfOperationFilteredSummary) => this.shouldShowFullLiquidation(item),
      {
        // Card 3902: o menu da linha só oferece o que a pessoa pode gravar (mesmas claims da Gestão v2).
        canUpdate: () => this.derivativeActionPermissions.canUpdate(),
        canDeleteOperation: () => this.derivativeActionPermissions.canDeleteOperation(),
        canLiquidate: () => this.derivativeActionPermissions.canLiquidate(),
        canCreateCommittee: () => this.derivativeActionPermissions.canCreateCommittee(),
      }
    );
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (changes['route']) {
      console.log('changes', changes);
    }
  }

  async ngOnInit() {
    this.viewMode = 'table'; // Set table view as default

    // Inicializa os filtros a partir da URL
    const queryParams = this.route.snapshot.queryParams;

    if (Object.keys(queryParams).length > 0) {
      // Inicializar o search a partir da URL
      this.search = queryParams['filter'] || '';

      // Converter query params para filtros
      const filters: NdfOperationFilterRequest = {};

      Object.keys(queryParams).forEach(key => {
        if (['companyId', 'companyIds', 'CompanyId', 'COMPANYID'].includes(key)) {
          return;
        }
        const value = queryParams[key];
        if (value !== undefined && value !== null && value !== '') {
          // Converter arrays de volta para arrays se necessário
          if (
            (key.includes('Ids') || key === 'operationStrategyTypes') &&
            typeof value === 'string'
          ) {
            (filters as Record<string, unknown>)[key] = value.split(',');
          } else if (key === 'withoutInstallments') {
            // Converter string para boolean
            (filters as Record<string, unknown>)[key] =
              value === 'true' ? true : value === 'false' ? false : undefined;
          } else {
            (filters as Record<string, unknown>)[key] = value;
          }
        }
      });

      this.currentFilters = Object.keys(filters).length > 0 ? filters : null;

      // Aplicar os filtros ao ndfOperationFilter
      if (this.currentFilters) {
        this.ndfOperationFilter = {
          ...this.ndfOperationFilter,
          internationalCurrencyId: this.currentFilters.internationalCurrencyId,
          financialInstitutionId: this.currentFilters.financialInstitutionId,
          financialInstitutionName: this.currentFilters.financialInstitutionName,
          operationStatus: this.currentFilters.operationStatus,
          financialStatus: this.currentFilters.financialStatus,
          composition: this.currentFilters.composition,
          operationStrategyTypes: this.currentFilters.operationStrategyTypes || [],
          ...(this.currentFilters.withoutInstallments !== undefined && {
            withoutInstallments: this.currentFilters.withoutInstallments,
          }),
          valueMEFrom: this.currentFilters.valueMEFrom,
          valueMETo: this.currentFilters.valueMETo,
          daysCountFrom: this.currentFilters.daysCountFrom,
          daysCountTo: this.currentFilters.daysCountTo,
          notionalAmountFrom: this.currentFilters.notionalAmountFrom,
          notionalAmountTo: this.currentFilters.notionalAmountTo,
          forwardRateFrom: this.currentFilters.forwardRateFrom,
          forwardRateTo: this.currentFilters.forwardRateTo,
          capValueFrom: this.currentFilters.capValueFrom,
          capValueTo: this.currentFilters.capValueTo,
          referenceExchangeRateFrom: this.currentFilters.referenceExchangeRateFrom,
          referenceExchangeRateTo: this.currentFilters.referenceExchangeRateTo,
          dueAtFrom: this.currentFilters.dueAtFrom
            ? this.currentFilters.dueAtFrom.split('T')[0]
            : undefined,
          dueAtTo: this.currentFilters.dueAtTo
            ? this.currentFilters.dueAtTo.split('T')[0]
            : undefined,
          operationDateFrom: this.currentFilters.operationDateFrom
            ? this.currentFilters.operationDateFrom.split('T')[0]
            : undefined,
          operationDateTo: this.currentFilters.operationDateTo
            ? this.currentFilters.operationDateTo.split('T')[0]
            : undefined,
          remarks: this.currentFilters.remarks,
          filter: this.search,
        };

        // Define o tabViewActiveIndex com base no operationStatus
        if (this.currentFilters.operationStatus) {
          switch (this.currentFilters.operationStatus) {
            case this.operationStatus.Simulated:
              this.tabViewActiveIndex = 1;
              break;
            case this.operationStatus.Quoted:
              this.tabViewActiveIndex = 2;
              break;
            case this.operationStatus.Hired:
              this.tabViewActiveIndex = 3;
              break;
            default:
              this.tabViewActiveIndex = 0;
          }
        }

        // Remover campos vazios do ndfOperationFilter
        Object.keys(this.ndfOperationFilter).forEach(key => {
          const value = this.ndfOperationFilter[key as keyof NdfOperationsFilteredSummaryParams];
          if (
            value === '' ||
            value === null ||
            value === undefined ||
            (typeof value === 'boolean' && !value) ||
            (Array.isArray(value) && value.length === 0 && key !== 'operationStrategyTypes')
          ) {
            delete this.ndfOperationFilter[key as keyof NdfOperationsFilteredSummaryParams];
          }
        });
      } else if (this.search) {
        // Se só há search, aplicar ao ndfOperationFilter
        this.ndfOperationFilter = {
          filter: this.search,
        };
      }
    } else {
      this.currentFilters = null;
      this.search = '';
    }

    if (this.store) {
      this.dateFormat = (await this.store
        .select(selectDateFormat)
        .pipe(first())
        .toPromise()) as string;
    }
    this.breadcrumbs = [
      {
        label: this.t(this.tk.Management.Management),
        routerLink: '/dashboard',
      },
      {
        label: this.t(this.tk.Management.Derivatives),
      },
      {
        label: this.t(this.tk.Management.NdfDerivativesManagement),
        routerLink: '/dashboard/ndf-operation/list',
      },
    ];
    this.fillTabViewItems();
    this.initCompanySelection();

    // Sincroniza com mudanças no sele��o do usu�rio
    // O seletor local apenas atualiza selectedCompanyIds para que o seletor local reflita as empresas selecionadas
    // O conteúdo só é atualizado quando o usuário interage com o seletor local (via handleOptionChange)
    if (this.store) {
      this.store
        .select(selectPageFilterCompanyIds)
        .pipe(
          skip(1), // Ignora emissão inicial (já tratada por initCompanySelection())
          distinctUntilChanged(
            (a, b) =>
              JSON.stringify([...(a ?? [])].sort()) === JSON.stringify([...(b ?? [])].sort())
          ),
          takeUntil(this.destroy$)
        )
        .subscribe(companyIds => {
          const normalizedCompanyIds = companyIds ?? [];
          this.selectedCompanyIds = normalizedCompanyIds;
          this.ndfOperationFilter = {
            ...this.ndfOperationFilter,
            companysIds: normalizedCompanyIds,
          };
          this.resetPagination();
          this.getNdfOperations();
          this.changeDetectorRef.markForCheck();
        });
    }

    this.getInternationalCurrencies();
    this.getAllQuotesForDate();
    this.getFilterMaxValue();
    this.simulateSaleOperationSubject.pipe(debounceTime(500)).subscribe(() => {
      this.simulateSaleOperation();
    });

    // Inicializar os filtros ativos se houver search
    if (this.search && this.search.trim() !== '') {
      this.activeFilterConfigs = [
        {
          propertyName: 'filter',
          label: 'filter',
          type: 'text',
          value: this.search,
          group: 'Pesquisa',
        },
      ];
    }
  }

  /** A primeira resposta da listagem já chegou (controla esqueleto x "Sem operações cadastradas"). */
  operationsLoaded = false;

  getNdfOperations(): void {
    // Primeira carga: o esqueleto da tabela já indica o carregamento, sem o loading global por cima.
    const showGlobalLoading = this.operationsLoaded;
    if (showGlobalLoading) this.utilsService.setIsLoading(true);

    const params = {
      ...this.ndfOperationFilter,
      pageSize: this.paginationRequest?.PageSize || 10,
      page: (this.paginationRequest?.Page || 0) + 1,
      sortColumn: this.paginationRequest?.SortBy || '',
      sortDirection: this.paginationRequest?.SortDirection || '',
      includeInstallments: true,
    };

    this.ndfCalculatorService.getNdfOperationsFilteredSummary(params).subscribe({
      next: response => {
        if (showGlobalLoading) this.utilsService.setIsLoading(false);
        this.operationsLoaded = true;
        if (response) {
          this.ndfOperations = response.data as unknown as NdfOperationFilteredSummary[];
          const { total, currentPage, pages, itensPerPage } = response as unknown as Pagination;
          this.pagination = {
            total,
            currentPage,
            pages,
            itemsPerPage: itensPerPage as number,
          };
          // Limpar cache de menus quando os dados mudam
          this.actionsMixin.clearCache();
          this.changeDetectorRef.markForCheck();
        }
      },
      error: error => {
        if (showGlobalLoading) this.utilsService.setIsLoading(false);
        this.operationsLoaded = true;
        this.changeDetectorRef.markForCheck();
        this.toast.showToastError(
          this.t(this.tk.Management.ErrorLoadingOperations),
          error?.errors?.length ? error.errors[0].message : this.t(this.tk.General.RequestError)
        );
      },
      complete: () => {
        this.getStatusCounts();
      },
    });
  }

  getStatusCounts(): void {
    const params = { ...this.ndfOperationFilter };

    // Removemos apenas o operationStatus para contar todos os status
    if (params.operationStatus) {
      delete params.operationStatus;
    }

    this.ndfCalculatorService.getNdfOperationsStatusCounts(params).subscribe({
      next: response => {
        const { Simulated = 0, Quoted = 0, Hired = 0 } = response as StatusCount;

        const updatedCounts: OperationCount = {
          all: Simulated + Quoted + Hired,
          simulated: Simulated,
          quoted: Quoted,
          hired: Hired,
        };

        this.tabViewItems = this.tabViewItems.map((item: TabViewItem) => ({
          ...item,
          count: updatedCounts[item.id as keyof OperationCount] ?? item.count,
        }));
      },
      error: error => {
        console.error('Error getting status counts: ', error);
        this.toast.showToastError(
          this.t(this.tk.Management.ErrorLoadingOperationsStatusCount),
          error?.errors?.length ? error.errors[0].message : this.t(this.tk.General.RequestError)
        );
      },
    });
  }

  initCompanySelection(): void {
    if (!this.store) {
      this.getNdfOperations();
      return;
    }

    // Garantir que o store global tenha empresas populadas para que o app-select-company possa usar o filtro
    this.ensureCompanyOptionsLoaded();

    // Popula companyOptions para resolução de labels nos filtros ativos
    of([] as Option[])
      .pipe(first(opts => opts.length > 0))
      .subscribe(opts => {
        this.companyOptions = opts;
      });

    // Prioridade: URL > filtro local compartilhado > companyId do auth store.
    // `companyId=all` (card 3571): todas as empresas conhecidas; sem a lista ainda, abre sem filtro (todas) e o
    // app-select-company emite a selecao completa quando carregar.
    const urlParams = this.route.snapshot.queryParams;
    const urlCompanyIds = this.companySelection.resolveUrlCompanyIds(urlParams);
    if (urlCompanyIds.length > 0 || this.companySelection.isAllRequested(urlParams)) {
      this.selectedCompanyIds = urlCompanyIds;
      this.ndfOperationFilter = { ...this.ndfOperationFilter, companysIds: urlCompanyIds };
      this.getNdfOperations();
    } else {
      this.store!.select(selectPageFilterCompanyIds)
        .pipe(take(1))
        .subscribe(globalIds => {
          const normalizedGlobalIds = globalIds ?? [];
          if (normalizedGlobalIds.length > 0) {
            this.selectedCompanyIds = normalizedGlobalIds;
            this.ndfOperationFilter = {
              ...this.ndfOperationFilter,
              companysIds: normalizedGlobalIds,
            };
          } else {
            this.store!.select(selectCompanyId)
              .pipe(take(1))
              .subscribe(companyId => {
                if (companyId) {
                  this.selectedCompanyIds = [companyId];
                  this.ndfOperationFilter = {
                    ...this.ndfOperationFilter,
                    companysIds: [companyId],
                  };
                }
              });
          }
          this.getNdfOperations();
        });
    }
  }

  /**
   * Garante que o store global tenha empresas populadas para que o app-select-company possa usar o filtro
   * Só popula se o store ainda não tiver empresas (para não sobrescrever se o seletor local já carregou)
   */
  private ensureCompanyOptionsLoaded(): void {
    if (!this.store) {
      return;
    }

    of([] as Option[])
      .pipe(take(1), takeUntil(this.destroy$))
      .subscribe(globalOptions => {
        if (!globalOptions || globalOptions.length === 0) {
          // Se o store não tem empresas, carregar da API e popular
          this.companyService
            .get({ page: 1, pageSize: 1000 })
            .pipe(takeUntil(this.destroy$))
            .subscribe({
              next: response => {
                this.companyDataService.mapCompanyResponseToOptionsData(response);
              },
              error: error => {
                console.error('Erro ao carregar empresas para popular store global:', error);
              },
            });
        }
      });
  }

  // Atualiza a URL com o companyId em CSV para suportar multiempresa
  private updateUrlWithCompanyIds(companyIds: string[]) {
    this.router.navigate([], {
      relativeTo: this.route,
      queryParams: this.companySelection.buildQueryParam(companyIds),
      queryParamsHandling: 'merge',
      replaceUrl: true,
    });
  }

  handleCardOperationSummaryAction(actionOperationSummary: CardOperationSummaryAction) {
    const { action, item, subAction } = actionOperationSummary;
    switch (action) {
      case 'edit':
        this.router.navigate(['/dashboard/control/derivative-management/derivatives/list'], {
          queryParams: {
            demandId: item?.derivativeOperationDemandId || '',
            operationId: item?.id || '',
          },
        });
        break;
      case 'remove':
        this.confirmDialogService
          .confirm(
            '',
            this.t(this.tk.Management.OperationTitleDeleteConfirmation),
            this.t(this.tk.General.Confirm),
            this.t(this.tk.General.Cancel),
            '',
            'operation-should-remove'
          )
          .then(response => {
            if (response && item) {
              this.removeOperation(item as NdfOperationFilteredSummary);
            }
          });
        break;
      case 'toHire':
        this.handleHireOperation(item);
        break;
      case 'toSale':
        this.resetSaleOperation();
        switch (subAction) {
          case 'full':
            this.liquidationType = subAction;
            this.ndfCalculatorService
              .getNdfPreviousDayPTaxLessHoliday(
                item?.internationalCurrencyId || '',
                item?.dueAt || ''
              )
              .subscribe({
                next: response => {
                  this.ptaxQuotationDate = response?.quoteDate as unknown as string;
                  this.settlementRate = response?.saleRate || 0;
                  this.operationSaleForm.patchValue({
                    [this.operationSaleProps.settlementRate]: response?.saleRate.toString() || '0',
                  });
                },
                error: error => {
                  console.error('getNdfPreviousDayPtax error', error);
                  this.toast.showToastError(
                    this.t(this.tk.Management.ErrorLoadingPreviousDayPtax),
                    error?.errors?.length
                      ? error.errors[0].message
                      : this.t(this.tk.General.RequestError)
                  );
                },
                complete: () => {
                  this.operationSaleForm.patchValue({
                    [this.operationSaleProps.settlementDate]: item?.dueAt,
                  });
                  this.dateTimeSale = item?.dueAt || '';
                  this.settlementNotional = item?.notionalAmount || 0;
                  this.operationSaleForm.patchValue({
                    [this.operationSaleProps.settlementNotional]:
                      item?.notionalAmount?.toString() || '0',
                  });

                  this.simulateSaleOperationSubject.next();
                },
              });
            break;
          case 'partial-before':
            this.liquidationType = subAction;
            this.settlementNotional = item?.notionalAmount || 0;
            this.operationSaleForm.patchValue({
              [this.operationSaleProps.settlementNotional]: item?.notionalAmount?.toString() || '0',
            });
            this.operationSaleForm.patchValue({
              [this.operationSaleProps.settlementDate]: '',
            });
            this.dateTimeSale = '';
            this.simulateSaleOperationSubject.next();
            break;
          case 'partial':
            this.liquidationType = subAction;
            break;
        }
        this.handleSaleOperation(item);
        break;
      case 'create-committee':
        if (item) {
          this.openCreateCommitteeModal(item);
        }
        break;
      case 'toQuote':
        this.handleQuoteOperation(item);
        break;
      case 'revert-hired-to-quoted':
        this.handleRevertHiredToQuoteOperation(item as NdfOperationFilteredSummary);
        break;
      case 'toSimulated':
        this.handleSimulatedOperation(item);
        break;
      case 'revert-liquidation-to-open':
        if (!item?.id) return;
        this.utilsService.setIsLoading(true);
        this.getNdfOperationDetailSubscription(item.id).subscribe({
          next: response => {
            this.utilsService.setIsLoading(false);
            if (response) {
              this.operationToHire = response;
              this.operationToHireSummary =
                this.ndfOperations.find(operation => operation.id === this.operationToHire?.id) ||
                null;
              this.operationToHireSummary = {
                ...this.operationToHireSummary,
                referenceExchangeRate: this.operationToHire?.referenceExchangeRate || 0,
              };
              const modalRef = this.dialogService.open(ModalConfirmComponent, {
                data: {
                  title: this.t(this.tk.Management.StatusReversion),
                  description: this.t(
                    this.tk.Management.NdfOperationTitleRevertLiquidationConfirmation
                  ),
                  btnFirst: this.t(this.tk.General.Cancel),
                  btnSecond: this.t(this.tk.General.Confirm),
                },
              });

              if (modalRef) {
                modalRef.onClose.subscribe({
                  next: result => {
                    if (result?.confirmed && item) {
                      this.revertLiquidationToOpen(item);
                    }
                  },
                });
              }
            }
          },
          error: error => {
            this.utilsService.setIsLoading(false);
            console.error('Error getting NdfOperationDetail: ', error);
            this.toast.showToastError(
              this.t(this.tk.Management.ErrorLoadingOperation),
              error?.errors?.length ? error.errors[0].message : this.t(this.tk.General.RequestError)
            );
          },
        });
        break;
      default:
        break;
    }
  }

  revertLiquidationToOpen(operation: NdfOperationFilteredSummary) {
    if (operation.id) {
      this.utilsService.setIsLoading(true);
      this.ndfCalculatorService.putNdfRevertLiquidate(operation?.id as string).subscribe({
        next: () => {
          this.utilsService.setIsLoading(false);
          this.toast.showToastSuccess(this.t(this.tk.Management.NdfOperationRevertedSuccessfully));
          this.resetHireOperation();
          this.handleSearch(operation.sequentialId, true);
        },
        error: error => {
          this.utilsService.setIsLoading(false);
          console.error('Error reverting operation: ', error);
          this.toast.showToastError(
            this.t(this.tk.Management.NdfErrorRevertingOperation),
            error?.errors?.length ? error.errors[0].message : this.t(this.tk.General.RequestError)
          );
        },
      });
    }
  }
  handleRevertHiredToQuoteOperation(operation: NdfOperationFilteredSummary) {
    if (!operation.id) return;

    const modalRef = this.dialogService.open(ModalConfirmComponent, {
      data: {
        title: this.t(this.tk.Management.StatusReversion),
        description: this.t(this.tk.Management.ConfirmRevertContractStatusQuoted),
        btnFirst: this.t(this.tk.General.Cancel),
        btnSecond: this.t(this.tk.General.Confirm),
      },
    });

    if (modalRef) {
      modalRef.onClose.subscribe({
        next: result => {
          if (result?.confirmed) {
            this.handleQuoteOperation(operation);
          }
        },
      });
    }
  }
  handleSimulatedOperation(item: NdfOperationFilteredSummary | null) {
    if (!item?.id) return;

    const modalRef = this.dialogService.open(ModalConfirmComponent, {
      data: {
        title: this.t(this.tk.Management.StatusReversion),
        description: this.t(this.tk.Management.ConfirmReversalQuotationToSimulated),
        btnFirst: this.t(this.tk.General.Cancel),
        btnSecond: this.t(this.tk.General.Confirm),
      },
    });

    if (modalRef) {
      modalRef.onClose.subscribe({
        next: result => {
          if (result?.confirmed) {
            this.ndfCalculatorService.putOperationStatusSimulated(item.id as string).subscribe({
              next: () => {
                this.utilsService.setIsLoading(false);
                this.getNdfOperations();
              },
              error: error => {
                this.utilsService.setIsLoading(false);
                this.toast.showToastError(
                  this.t(this.tk.Management.ErrorLoadingOperation),
                  error?.errors?.length
                    ? error.errors[0].message
                    : this.t(this.tk.General.RequestError)
                );
              },
            });
          }
        },
      });
    }
  }
  handleSaleOperation(item: NdfOperationFilteredSummary | null) {
    if (item) {
      this.operationToSaleSummary = item;
      this.getNdfOperationDetailSubscription(item.id as string).subscribe({
        next: operation => {
          if (operation) {
            this.operationToSale = operation;

            // Load DI for SWAP operations
            // if (item.operationStrategyType === 'SWAP') {
            //   if (this.operationToSale.operationDate && this.operationToSale.settlementDate) {
            //     this.ndfCalculatorService
            //       .getSettlementDi(
            //         this.operationToSale.operationDate,
            //         this.operationToSale.settlementDate
            //       )
            //       .subscribe({
            //         next: di => {
            //           if (di !== null) {
            //             this.operationSaleForm.patchValue({ settlementDi: di });
            //           }
            //         },
            //         error: error => {
            //           console.error('Error loading settlement DI:', error);
            //           this.toast.showToastError(
            //             this.t(this.tk.Management.ErrorLoadingDi),
            //             error?.errors?.length
            //               ? error.errors[0].message
            //               : this.t(this.tk.General.RequestError)
            //           );
            //         },
            //       });
            //   }
            // }

            this.openSaleDialog();
          }
        },
        error: error => {
          this.utilsService.setIsLoading(false);
          console.error('Error getting NdfOperationDetail: ', error);
          this.toast.showToastError(
            this.t(this.tk.Management.ErrorLoadingOperation),
            error?.errors?.length ? error.errors[0].message : this.t(this.tk.General.RequestError)
          );
        },
      });
    }
  }

  handleQuoteOperation(item: NdfOperationFilteredSummary | null) {
    if (!item?.id) return;
    this.dateTimeQuoted = this.currentDate.toISOString();
    this.operationQuoteForm.patchValue({
      [this.operationQuoteHireProps.data]: this.dateTimeQuoted,
    });
    this.operationToHire = null;
    this.operationToHireSummary = item;
    if (this.quoteOperationDialogRef) {
      this.quoteOperationDialogRef.open(
        '',
        '',
        '',
        this.t(this.tk.General.Cancel) || '',
        '',
        'quote-operation',
        false,
        false // acceptVisible = false para não mostrar o botão automático
      );
    }
  }

  onQuoteDialogConfirm() {
    if (this.dateTimeQuoted && this.operationQuoteForm.valid && this.operationToHireSummary?.id) {
      this.utilsService.setIsLoading(true);
      this.ndfCalculatorService
        .putQuoteOperation(
          this.operationToHireSummary.id as string,
          this.dateTimeQuoted,
          this.operationQuoteForm.get(this.operationQuoteHireProps.bankOperatorName)?.value
        )
        .subscribe({
          next: () => {
            this.dateTimeQuoted = '';
            this.operationQuoteForm.reset();
            this.utilsService.setIsLoading(false);
            this.toast.showToastSuccess(this.t(this.tk.Management.NdfQuoteSavedSuccessfully));
            this.quoteOperationDialogRef?.close();
            this.handleSearch(this.operationToHireSummary?.sequentialId, true);
          },
          error: error => {
            this.utilsService.setIsLoading(false);
            console.error('Error quoting operation: ', error);

            this.toast.showToastError(
              this.t(this.tk.Management.ErrorUpdatingOperation),
              error?.errors?.length ? error.errors[0].message : this.t(this.tk.General.RequestError)
            );
          },
        });
    } else {
      this.toast.showToastError(
        this.t(this.tk.Management.NdfFormInvalid),
        this.t(this.tk.Management.NdfPleaseCheckFields)
      );
    }
  }

  removeOperation(operation: NdfOperationFilteredSummary) {
    if (operation.id) {
      this.utilsService.setIsLoading(true);
      this.ndfCalculatorService.deleteOperation(operation.id as string).subscribe({
        next: () => {
          this.utilsService.setIsLoading(false);
          this.toast.showToastSuccess(this.t(this.tk.Management.OperationRemovedSuccessfully), '');
          this.getNdfOperations();
        },
        error: (error: ApiResponse<NdfOperationFilteredSummary>) => {
          this.utilsService.setIsLoading(false);
          console.error('Error removing operation: ', error);
          this.toast.showToastError(
            this.t(this.tk.Management.ErrorRemovingOperation),
            error?.errors?.length ? error.errors[0].message : this.t(this.tk.General.RequestError)
          );
        },
      });
    }
  }

  handleHireOperation(item: NdfOperationFilteredSummary | null) {
    if (!item?.id) return;
    this.utilsService.setIsLoading(true);
    this.getNdfOperationDetailSubscription(item.id).subscribe({
      next: response => {
        this.utilsService.setIsLoading(false);
        if (response) {
          this.operationToHire = response;
          this.operationToHireSummary =
            this.ndfOperations.find(operation => operation.id === this.operationToHire?.id) || null;
          this.operationToHireSummary = {
            ...this.operationToHireSummary,
            referenceExchangeRate: this.operationToHire?.referenceExchangeRate || 0,
          };

          this.derivativeCardInfo =
            this.derivativesCardInfoDataService.mapOperationFilteredToDerivativeCardInfo(
              this.operationToHireSummary
            );

          this.derivativeSummary =
            this.derivativesSummaryDataService.mapOperationFilteredToDerivativeSummary(
              this.operationToHireSummary
            );

          this.openHireDialog();
        }
      },
      error: error => {
        this.utilsService.setIsLoading(false);
        console.error('Error getting NdfOperationDetail: ', error);
        this.toast.showToastError(
          this.t(this.tk.Management.ErrorLoadingOperation),
          error?.errors?.length ? error.errors[0].message : this.t(this.tk.General.RequestError)
        );
      },
    });
  }

  getNdfOperationDetailSubscription(
    id: string
  ): Observable<NdfDemandsCalculationsOperation | null> {
    return this.ndfCalculatorService.getNdfOperation(id);
  }

  handleEarlyReversalLiquidation() {
    if (this.operationToSale?.id) {
      const settlementDate = this.operationSaleForm.get(
        this.operationSaleProps.settlementDate
      )?.value;
      const settlementNotional = this.operationSaleForm.get(
        this.operationSaleProps.settlementNotional
      )?.value;
      const settlementRate = this.operationSaleForm.get(
        this.operationSaleProps.settlementRate
      )?.value;
      const settlementSpot = this.operationSaleForm.get(
        this.operationSaleProps.settlementSpot
      )?.value;

      const settlementDateControl = this.operationSaleForm.get(
        this.operationSaleProps.settlementDate
      );
      const settlementRateControl = this.operationSaleForm.get(
        this.operationSaleProps.settlementRate
      );

      settlementDateControl?.markAsTouched();
      settlementRateControl?.markAsTouched();
      settlementDateControl?.updateValueAndValidity();
      settlementRateControl?.updateValueAndValidity();

      const isMissingSettlementDate =
        settlementDate === null || settlementDate === undefined || settlementDate === '';
      const isMissingSettlementRate =
        settlementRate === null || settlementRate === undefined || settlementRate === '';

      if (isMissingSettlementDate || isMissingSettlementRate) {
        this.changeDetectorRef.markForCheck();
        return;
      }

      const currentNetAmount =
        Number(this.operationSaleForm.get(this.operationSaleProps.netAmount)?.value ?? 0) || 0;
      const formattedSettlementDate = this.dateFormatter.formatDateInstant(settlementDate, true);
      const numberFormatter = new Intl.NumberFormat('pt-BR', {
        minimumFractionDigits: 2,
        maximumFractionDigits: 6,
      });
      this.pendingEarlyReversalPayload = {
        operationId: this.operationToSale.id,
        settlementDate: String(settlementDate),
        settlementNotional: Number(settlementNotional),
        settlementRate: Number(settlementRate),
        settlementSpot: Number(settlementSpot),
      };

      const modalData: SaveReversalConfirmationData = {
        settlementDate: formattedSettlementDate || String(settlementDate),
        settlementNotional: numberFormatter.format(Number(settlementNotional)),
        settlementSpot: numberFormatter.format(Number(settlementSpot)),
        settlementRate: numberFormatter.format(Number(settlementRate)),
        netAmount: Number(currentNetAmount),
      };

      this.saveReversalConfirmationModalRef?.open(modalData);
    } else {
      this.toast.showToastError(
        this.t(this.tk.Management.NdfFormInvalid),
        this.t(this.tk.Management.NdfPleaseCheckFields)
      );
    }
  }

  onSaveReversalConfirmed(): void {
    if (!this.pendingEarlyReversalPayload) return;

    const payload = this.pendingEarlyReversalPayload;
    this.pendingEarlyReversalPayload = null;

    this.utilsService.setIsLoading(true);
    this.ndfCalculatorService
      .putNdfLiquidateEarlyReversal(
        payload.operationId,
        payload.settlementDate,
        payload.settlementNotional,
        payload.settlementRate,
        payload.settlementSpot
      )
      .subscribe({
        next: response => {
          this.utilsService.setIsLoading(false);

          if (response) {
            this.toast.showToastSuccess(
              this.t(this.tk.Management.NdfEarlyReversalLiquidationSuccess)
            );
            this.resetSaleOperation();
            this.handleSearch(this.operationToSale?.sequentialId, true);
          }
        },
        error: error => {
          console.error('API Error:', error);
          this.utilsService.setIsLoading(false);
          this.toast.showToastError(
            this.t(this.tk.Management.NdfEarlyReversalLiquidationError),
            error?.errors?.length ? error.errors[0].message : this.t(this.tk.General.RequestError)
          );
        },
      });
  }

  openSaleDialog() {
    this.liquidateOperationModalRef?.open();
  }

  onLiquidateOperationRequested(): void {
    if (!this.operationToSale?.id) {
      this.toast.showToastError(
        this.t(this.tk.Management.NdfFormInvalid),
        this.t(this.tk.Management.NdfPleaseCheckFields)
      );
      return;
    }

    const settlementDateControl = this.operationSaleForm.get(
      this.operationSaleProps.settlementDate
    );
    const settlementRateControl = this.operationSaleForm.get(
      this.operationSaleProps.settlementRate
    );

    settlementDateControl?.markAsTouched();
    settlementRateControl?.markAsTouched();
    settlementDateControl?.updateValueAndValidity();
    settlementRateControl?.updateValueAndValidity();

    const settlementDate = settlementDateControl?.value;
    const settlementNotional =
      this.operationSaleForm.get(this.operationSaleProps.settlementNotional)?.value ?? 0;
    const settlementRate = settlementRateControl?.value;
    const settlementSpot =
      this.operationSaleForm.get(this.operationSaleProps.settlementSpot)?.value ?? 0;

    const isMissingSettlementDate =
      settlementDate === null || settlementDate === undefined || settlementDate === '';
    const isMissingSettlementRate =
      settlementRate === null || settlementRate === undefined || settlementRate === '';

    if (isMissingSettlementDate || isMissingSettlementRate) {
      this.changeDetectorRef.markForCheck();
      return;
    }

    const formattedSettlementDate = this.dateFormatter.formatDateInstant(settlementDate, true);
    const numberFormatter = new Intl.NumberFormat('pt-BR', {
      minimumFractionDigits: 2,
      maximumFractionDigits: 6,
    });

    const modalData: ConfirmLiquidationData = {
      settlementDate: formattedSettlementDate || String(settlementDate),
      settlementNotional: numberFormatter.format(Number(settlementNotional)),
      settlementSpot: numberFormatter.format(Number(settlementSpot)),
      settlementRate: numberFormatter.format(Number(settlementRate)),
      totalAmount: Number(
        this.operationSaleForm.get(this.operationSaleProps.totalAmount)?.value ?? 0
      ),
      withholdingTax: Number(
        this.operationSaleForm.get(this.operationSaleProps.withholdingTax)?.value ?? 0
      ),
      incomeTaxPayable: Number(
        this.operationSaleForm.get(this.operationSaleProps.incomeTaxPayable)?.value ?? 0
      ),
      netAmount: Number(this.operationSaleForm.get(this.operationSaleProps.netAmount)?.value ?? 0),
    };

    this.confirmLiquidationModalRef?.open(modalData);
  }

  onSaleDialogConfirm() {
    if (this.operationSaleForm.valid && this.operationToSale?.id) {
      this.utilsService.setIsLoading(true);
      this.ndfCalculatorService
        .putSaleOperation(
          this.operationToSale?.id as string,
          this.dateTimeSale,
          this.settlementNotional,
          this.settlementRate,
          this.settlementSpot
        )
        .subscribe({
          next: () => {
            this.utilsService.setIsLoading(false);
            this.toast.showToastSuccess(this.t(this.tk.Management.NdfOperationSuccessfullySelled));
            this.resetSaleOperation();
            this.liquidateOperationModalRef?.close();
            this.handleSearch(this.operationToSale?.sequentialId, true);
          },
          error: error => {
            this.utilsService.setIsLoading(false);
            console.error('putSaleOperation error: ', error);
            this.toast.showToastError(
              this.t(this.tk.Management.ErrorUpdatingOperation),
              error?.errors?.length ? error.errors[0].message : this.t(this.tk.General.RequestError)
            );
          },
        });
    } else {
      this.toast.showToastError(
        this.t(this.tk.Management.NdfFormInvalid),
        this.t(this.tk.Management.NdfPleaseCheckFields)
      );
    }
  }

  openHireDialog() {
    if (this.hireOperationDialogRef) {
      this.hireOperationDialogRef.open(
        '',
        '',
        '',
        this.t(this.tk.General.Cancel) || '',
        '',
        'hire-operation',
        false,
        false // acceptVisible = false para não mostrar o botão automático
      );
    }
  }

  onHireDialogConfirm() {
    if (this.dateTimeHired && this.operationToHire?.id && this.operationHireForm.valid) {
      this.utilsService.setIsLoading(true);
      this.ndfCalculatorService
        .putHireOperation(
          this.operationToHire.id,
          this.dateTimeHired,
          this.operationHireForm.get(this.operationQuoteHireProps.bankOperatorName)?.value
        )
        .subscribe({
          next: () => {
            this.utilsService.setIsLoading(false);
            this.quoteSuccessfullyContractedLabel =
              this.t(this.tk.Management.NdfQuotation) +
              ` ${this.operationToHire?.sequentialId} ` +
              this.t(this.tk.Management.NdfSuccessfullyContracted);

            this.toast.showToastSuccess(this.t(this.tk.Management.NdfSuccessfullyContracted));
            this.hireOperationDialogRef?.close();
            this.handleSearch(this.operationToHire?.sequentialId, true);
          },
          error: error => {
            this.utilsService.setIsLoading(false);
            console.error('putHireOperation error: ', error);

            this.toast.showToastError(
              this.t(this.tk.Management.ErrorUpdatingOperation),
              error?.errors?.length ? error.errors[0].message : this.t(this.tk.General.RequestError)
            );
          },
        });
    } else {
      this.toast.showToastError(
        this.t(this.tk.Management.NdfFormInvalid),
        this.t(this.tk.Management.NdfPleaseCheckFields)
      );
    }
  }

  simulateDI() {
    const isSwap = this.operationToSaleSummary?.operationStrategyType === 'SWAP';
    const settlementDate = this.operationSaleForm?.get(
      this.operationSaleProps.settlementDate
    )?.value;
    if (isSwap && this.operationToSale?.operationDate && settlementDate) {
      this.ndfCalculatorService
        .getSettlementDi(this.operationToSale.operationDate, settlementDate)
        .subscribe({
          next: di => {
            if (di !== null) {
              this.operationSaleForm.get(this.operationSaleProps.settlementDi)?.setValue(di);
              this.settlementDi = di;
            }
          },
          error: error => {
            console.error('Error loading settlement DI:', error);
            this.toast.showToastError(
              this.t(this.tk.Management.ErrorLoadingDi),
              error?.errors?.length ? error.errors[0].message : this.t(this.tk.General.RequestError)
            );
          },
        });
    }
  }
  simulateSaleOperation() {
    if (this.operationToSale && this.operationSaleForm.valid) {
      this.ndfCalculatorService
        .putSaleOperationSimulated(
          this.operationToSale?.id as string,
          this.dateTimeSale,
          this.settlementNotional,
          this.settlementRate,
          this.settlementSpot
        )
        .subscribe({
          next: response => {
            this.utilsService.setIsLoading(false);
            this.toast.showToastSuccess(
              this.t(this.tk.Management.OperationUpdatedSuccessfully),
              ''
            );
            if (response) {
              const {
                incomeTaxPayable,
                incomeTaxPayableRounded,
                netAmount,
                netAmountdRounded,
                settlementNotional,
                settlementRate,
                settlementSpot,
                totalAmount,
                totalAmountRounded,
                withholdingTax,
                withholdingTaxRounded,
              } = response as OperationSettleRequest;

              this.operationSaleForm.patchValue({
                [this.operationSaleProps.incomeTaxPayable]: incomeTaxPayable?.toString() || '',
                [this.operationSaleProps.incomeTaxPayableRounded]:
                  incomeTaxPayableRounded?.toString() || '',
                [this.operationSaleProps.netAmount]: netAmount?.toString() || '',
                [this.operationSaleProps.netAmountdRounded]: netAmountdRounded?.toString() || '',
                [this.operationSaleProps.settlementNotional]: settlementNotional?.toString() || '',
                [this.operationSaleProps.settlementRate]: settlementRate?.toString() || '',
                [this.operationSaleProps.settlementSpot]: settlementSpot?.toString() || '',
                [this.operationSaleProps.totalAmount]: totalAmount?.toString() || '',
                [this.operationSaleProps.totalAmountRounded]: totalAmountRounded?.toString() || '',
                [this.operationSaleProps.withholdingTax]: withholdingTax?.toString() || '',
                [this.operationSaleProps.withholdingTaxRounded]:
                  withholdingTaxRounded?.toString() || '',
              });
            }
          },
          error: error => {
            this.utilsService.setIsLoading(false);
            console.error('putSaleOperation error: ', error);

            this.toast.showToastError(
              this.t(this.tk.Management.ErrorUpdatingOperation),
              error?.errors?.length ? error.errors[0].message : this.t(this.tk.General.RequestError)
            );
          },
        });
    }
  }

  resetHireOperation() {
    this.quoteSuccessfullyContractedLabel = '';
    this.operationToHire = null;
    this.operationToHireSummary = null;
    this.dateTimeHired = '';
    this.operationHireForm.reset();
  }

  resetSaleOperation() {
    this.quoteSuccessfullyContractedLabel = '';
    this.operationToSale = null;
    this.operationToSaleSummary = null;
    this.dateTimeSale = '';
    this.settlementNotional = 0;
    this.settlementRate = 0;
    this.settlementSpot = 0;
    this.operationSaleForm.reset();
  }

  handleFilterPill(filter: Option): void {
    this.ndfOperationFilter = {
      ...this.ndfOperationFilter,
      [filter.key as keyof NdfOperationsFilteredSummaryParams]: '',
    };

    this.clearOptionFilter(filter.key as string);
    this.getNdfOperations();

    if (filter.key === 'financialInstitutionName') {
      this.handleFilterPill({ key: 'financialInstitutionId', label: '', value: '' });
    }
  }

  clearOptionFilter(filter: string) {
    switch (filter) {
      case 'companyId':
        this.selectedCompanyIds = [];
        break;

      case 'operationStatus':
        this.tabViewActiveIndex = 0;
        break;
      case 'filter':
        this.handleSearch('');
        break;
    }
  }

  getInternationalCurrencies(): void {
    this.utilsService.setIsLoading(true);
    this.ndfCalculatorService.getInternationalCurrencies().subscribe({
      next: response => {
        this.utilsService.setIsLoading(false);
        if (response) {
          this.currencyOptions = response.map(currency => ({
            label: currency.currencyCode,
            value: currency.id,
          }));
        }
      },
      error: error => {
        this.utilsService.setIsLoading(false);
        console.error('Error getting international currencies: ', error);

        this.toast.showToastError(
          this.t(this.tk.Management.ErrorLoadingInternacionalCurrencies),
          error?.errors?.length ? error.errors[0].message : this.t(this.tk.General.RequestError)
        );
      },
    });
  }

  getAllQuotesForDate(): void {
    this.utilsService.setIsLoading(true);
    this.ndfCalculatorService.GetAllQuotesForDateAsync().subscribe({
      next: response => {
        this.utilsService.setIsLoading(false);
        if (response) {
          this.ptaxQuotesDates = response;
        }
      },
      error: error => {
        this.utilsService.setIsLoading(false);
        console.error('Error getting PTAX Quotes: ', error);

        this.toast.showToastError(
          this.t(this.tk.Management.ErrorLoadingInternacionalCurrencies),
          error?.errors?.length ? error.errors[0].message : this.t(this.tk.General.RequestError)
        );
      },
    });
  }

  fillTabViewItems(): void {
    this.tabViewItems = [
      {
        title: this.t(this.tk.User.All),
        count: 0,
        tabindex: 0,
        content: '',
        id: 'all',
      },
      {
        title: this.t(this.tk.Management.NdfSimulations),
        count: 0,
        tabindex: 1,
        content: '',
        id: 'simulated',
      },
      {
        title: this.t(this.tk.Management.NdfListed),
        count: 0,
        tabindex: 2,
        content: '',
        id: 'quoted',
      },
      {
        title: this.t(this.tk.Management.NdfContractors),
        count: 0,
        tabindex: 3,
        content: '',
        id: 'hired',
      },
    ];
  }

  handleTabView(tabIndex: number): void {
    this.tabViewActiveIndex = tabIndex;

    const newOperationStatus = (() => {
      switch (this.tabViewActiveIndex) {
        case 0:
          return '';
        case 1:
          return this.operationStatus.Simulated;
        case 2:
          return this.operationStatus.Quoted;
        case 3:
          return this.operationStatus.Hired;
        default:
          return '';
      }
    })();

    this.ndfOperationFilter = {
      ...this.ndfOperationFilter,
      operationStatus: newOperationStatus,
    };

    // Sincroniza com currentFilters
    this.currentFilters = {
      ...this.currentFilters,
      operationStatus: newOperationStatus,
    };

    this.updateUrlWithFilters();
    this.getNdfOperations();
  }

  get activeFilters(): Option[] {
    const filters: Option[] = [];
    const filterKeys = Object.keys(this.ndfOperationFilter);

    const operationStatus = {
      Simulated: this.t(this.tk.Management.NdfSimulated),
      Quoted: this.t(this.tk.Management.NdfQuoted),
      Hired: this.t(this.tk.Management.NdfHired),
    };

    const financialStatus = {
      Open: this.t(this.tk.Management.NdfOpen),
      Settled: this.t(this.tk.Management.NdfSettled),
      Reversed: this.t(this.tk.Management.NdfSettledReversed),
    };

    const compositionStatus = {
      Full: this.t(this.tk.Management.NdfFull),
      Partial: this.t(this.tk.Management.NdfPartial),
    };

    const keysNotShow: string[] = [];

    filterKeys.forEach((key: string) => {
      const filter = this.ndfOperationFilter[key as keyof NdfOperationsFilteredSummaryParams];

      let label = filter as string;
      let group = '';

      switch (key) {
        case 'companyId':
          label = this.companyOptions.find(company => company.value === filter)?.label || key;
          group = this.t(this.tk.Management.Company);
          break;
        case 'companysIds': {
          const companyLabels = filter as string[];

          if (!companyLabels || !companyLabels.length) return;

          const labels = companyLabels
            ?.map(id => this.companyOptions.find(company => company.value === id)?.label)
            .filter(label => label) // Remove undefined values
            .join(', ');

          label = labels || key;
          group = this.t(this.tk.Management.user);

          break;
        }
        case 'financialInstitutionName':
          group = this.t(this.tk.Management.NdfBank);
          break;
        case 'financialInstitutionId':
          // Find the financial institution name from the currentFilters
          label = this.currentFilters?.financialInstitutionName || (filter as string);
          group = this.t(this.tk.Management.NdfBank);
          break;
        case 'internationalCurrencyId':
          label = this.currencyOptions.find(currency => currency.value === filter)?.label || key;
          group = this.t(this.tk.Management.NdfCurrency);
          break;
        case 'operationStatus':
          label = operationStatus[filter as keyof typeof operationStatus];
          group = this.t(this.tk.Management.NdfOperationStatus);
          break;
        case 'capValueFrom':
          label = this.currencyPipe.transform(filter as number, '', '', '1.2-2') || key;
          group = filters.find(f => f.key === 'capValueTo')?.group
            ? ''
            : this.t(this.tk.Management.NdfCap);
          break;
        case 'capValueTo':
          label = this.currencyPipe.transform(filter as number, '', '', '1.2-2') || key;
          group = filters.find(f => f.key === 'capValueFrom')?.group
            ? ''
            : this.t(this.tk.Management.NdfCap);
          break;
        case 'forwardRateFrom':
          label = this.currencyPipe.transform(filter as number, '', '', '1.2-2') || key;
          group = filters.find(f => f.key === 'forwardRateTo')?.group
            ? ''
            : this.t(this.tk.Management.NdfForward);
          break;
        case 'forwardRateTo':
          label = this.currencyPipe.transform(filter as number, '', '', '1.2-2') || key;
          group = filters.find(f => f.key === 'forwardRateFrom')?.group
            ? ''
            : this.t(this.tk.Management.NdfForward);
          break;
        case 'notionalAmountFrom':
          label = this.currencyPipe.transform(filter as number, '', '', '1.2-2') || key;
          group = filters.find(f => f.key === 'notionalAmountTo')?.group
            ? ''
            : this.t(this.tk.Management.NdfNotional);
          break;
        case 'notionalAmountTo':
          label = this.currencyPipe.transform(filter as number, '', '', '1.2-2') || key;
          group = filters.find(f => f.key === 'notionalAmountFrom')?.group
            ? ''
            : this.t(this.tk.Management.NdfNotional);
          break;
        case 'referenceExchangeRateFrom':
          label = this.currencyPipe.transform(filter as number, '', '', '1.2-2') || key;
          group = filters.find(f => f.key === 'referenceExchangeRateTo')?.group
            ? ''
            : this.t(this.tk.Management.NdfExchange);
          break;
        case 'referenceExchangeRateTo':
          label = this.currencyPipe.transform(filter as number, '', '', '1.2-2') || key;
          group = filters.find(f => f.key === 'referenceExchangeRateFrom')?.group
            ? ''
            : this.t(this.tk.Management.NdfExchange);
          break;
        case 'valueMEFrom':
          label = this.currencyPipe.transform(filter as number, '', '', '1.2-2') || key;
          group = filters.find(f => f.key === 'valueMETo')?.group
            ? ''
            : this.t(this.tk.Management.NdfValue);
          break;
        case 'valueMETo':
          label = this.currencyPipe.transform(filter as number, '', '', '1.2-2') || key;
          group = filters.find(f => f.key === 'valueMEFrom')?.group
            ? ''
            : this.t(this.tk.Management.NdfValue);
          break;
        case 'dueAtFrom':
          this.dateFormatter.formatDate(filter as string, true).subscribe({
            next: response => {
              label = response;
            },
          });
          group = filters.find(f => f.key === 'dueAtTo')?.group
            ? ''
            : this.t(this.tk.Management.NdfDueDate);
          break;
        case 'dueAtTo':
          this.dateFormatter.formatDate(filter as string, true).subscribe({
            next: response => {
              label = response;
            },
          });
          group = filters.find(f => f.key === 'dueAtFrom')?.group
            ? ''
            : this.t(this.tk.Management.NdfDueDate);
          break;
        case 'operationDateFrom':
          this.dateFormatter.formatDate(filter as string, true).subscribe({
            next: response => {
              label = response;
            },
          });
          group = filters.find(f => f.key === 'operationDateTo')?.group
            ? ''
            : this.t(this.tk.Management.NdfDateOfOperation);
          break;
        case 'operationDateTo':
          this.dateFormatter.formatDate(filter as string, true).subscribe({
            next: response => {
              label = response;
            },
          });
          group = filters.find(f => f.key === 'operationDateFrom')?.group
            ? ''
            : this.t(this.tk.Management.NdfDateOfOperation);
          break;
        case 'composition':
          label = compositionStatus[filter as keyof typeof compositionStatus];
          group = this.t(this.tk.Management.NdfComposition);
          break;
        case 'financialStatus':
          label = financialStatus[filter as keyof typeof financialStatus];
          group = this.t(this.tk.Management.NdfFinancialStatus);
          break;
        case 'remarks':
          group = this.t(this.tk.Management.NdfReference);
          break;
        case 'daysCountTo':
          group = filters.find(f => f.key === 'daysCountFrom')?.group
            ? ''
            : this.t(this.tk.Management.NdfCalendarDays);
          break;
        case 'daysCountFrom':
          group = filters.find(f => f.key === 'daysCountTo')?.group
            ? ''
            : this.t(this.tk.Management.NdfCalendarDays);
          break;
      }

      if (filter && !keysNotShow.includes(key)) {
        filters.push({
          label,
          key,
          value: filter as string,
          group,
        });
      }
    });

    return filters;
  }

  handleOptionChange(selectedOptions: Option[] | null, key: string): void {
    switch (key) {
      case 'selectedCompany':
        this.selectedCompanyIds = selectedOptions?.map(x => x.value) ?? [];
        this.ndfOperationFilter = {
          ...this.ndfOperationFilter,
          companysIds: this.selectedCompanyIds,
        };
        this.updateUrlWithCompanyIds(this.selectedCompanyIds);
        this.getNdfOperations();
        break;
    }
  }

  handleFilterActive(activeFilter: boolean): void {
    this.activeFilter = activeFilter;
  }

  handleSearch(search: string = '', resetOtherFilters: boolean = false): void {
    this.search = search;

    if (!resetOtherFilters) {
      if (search && search.trim() !== '') {
        this.ndfOperationFilter = {
          ...this.ndfOperationFilter,
          filter: search,
        };
      } else {
        // Remove filter from ndfOperationFilter when search is empty
        // eslint-disable-next-line @typescript-eslint/no-unused-vars
        const { filter: _, ...filterWithoutSearch } = this.ndfOperationFilter;
        this.ndfOperationFilter = filterWithoutSearch;
      }
    } else {
      this.resetFilter();
      if (search && search.trim() !== '') {
        this.ndfOperationFilter = {
          filter: search,
        };
      } else {
        this.ndfOperationFilter = {};
      }
    }

    // Atualiza a URL
    const queryParams: Record<string, string | number | boolean | null> = {};

    // Preserva outros parâmetros de consulta, exceto 'filter'
    for (const key in this.route.snapshot.queryParams) {
      if (key !== 'filter') {
        queryParams[key] = this.route.snapshot.queryParams[key];
      }
    }

    // Adiciona o filtro de pesquisa apenas se não estiver vazio
    if (search && search.trim() !== '') {
      queryParams['filter'] = search;
    }

    Object.assign(queryParams, this.companySelection.buildQueryParam(this.selectedCompanyIds));

    this.router.navigate([], {
      relativeTo: this.route,
      queryParams: queryParams,
      queryParamsHandling: 'replace' as QueryParamsHandling,
    });

    this.resetPagination();
    this.getNdfOperations();
  }

  /**
   * Resets the "page" property of "paginationRequest", "ndfOperationFilter" and p-paginator to default value
   */
  resetPagination(): void {
    this.ndfOperationFilter = {
      ...this.ndfOperationFilter,
      page: 0,
    };
    this.paginationRequest = {
      ...this.paginationRequest,
      Page: 0,
    };

    if (this.paginator) this.paginator.changePage(0);
  }

  handlePagination(pagination: PaginatorState) {
    this.paginationRequest = {
      Page: pagination.page,
      PageSize: pagination.rows,
    };
    this.getNdfOperations();
  }

  handleCalendarValue(dates: string[], action: string): void {
    switch (action) {
      case 'quote':
        this.dateTimeQuoted = dates[0];
        this.operationQuoteForm.patchValue({
          [this.operationQuoteHireProps.data]: this.dateTimeQuoted,
        });
        break;
      case 'hire':
        this.dateTimeHired = dates[0];
        this.operationHireForm.patchValue({
          [this.operationQuoteHireProps.data]: this.dateTimeHired,
        });
        break;
      case 'settlementDate':
        this.dateTimeSale = dates[0].split('T')[0] || '';
        this.operationSaleForm.patchValue({
          [this.operationSaleProps.settlementDate]: dates[0].split('T')[0] || '',
        });
        this.simulateSaleOperationSubject.next();
        // this.simulateDI();
        break;
    }
  }

  handleInputNumber(inputData: IdValue, key: string): void {
    const value = inputData?.value || 0;
    switch (key) {
      case 'settlementNotional':
        this.settlementNotional = value as number;
        this.operationSaleForm.patchValue({
          [this.operationSaleProps.settlementNotional]: value.toString(),
        });
        this.simulateSaleOperationSubject.next();
        break;
      case 'settlementRate':
        this.settlementRate = value as number;
        this.operationSaleForm.patchValue({
          [this.operationSaleProps.settlementRate]: value.toString(),
        });
        this.simulateSaleOperationSubject.next();
        break;
      case 'settlementSpot':
        this.settlementSpot = value as number;
        this.operationSaleForm.patchValue({
          [this.operationSaleProps.settlementSpot]: value.toString(),
        });
        this.simulateSaleOperationSubject.next();
        break;
      case 'settlementDi':
        this.settlementDi = value as number;
        this.operationSaleForm.patchValue({
          [this.operationSaleProps.settlementDi]: value.toString(),
        });
        this.simulateSaleOperationSubject.next();
        console.log('settlementDi', value);
        break;
    }
  }

  getFilterMaxValue(): void {
    this.utilsService.setIsLoading(true);
    this.ndfCalculatorService.getOperationsNdfMaxValue().subscribe({
      next: response => {
        this.utilsService.setIsLoading(false);
        this.operationMaxValue = response as number;
      },
      error: error => {
        this.utilsService.setIsLoading(false);
        console.error('getFilterMaxValue error: ', error);
        this.toast.showToastError(
          this.t(this.tk.Management.ErrorLoadingOperationMaxValue),
          error?.errors?.length ? error.errors[0].message : this.t(this.tk.General.RequestError)
        );
      },
    });
  }

  downloadFile(data: Blob, filename: string = 'base_dados') {
    const url = window.URL.createObjectURL(data);
    const link = document.createElement('a');
    link.href = url;
    link.download = `${filename}.xlsx`;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    window.URL.revokeObjectURL(url);
  }

  resetFilter(): void {
    this.ndfOperationFilter = {};
    this.selectedCompanyIds = [];
    this.search = '';
    this.handleFilterActive(false);
    this.tabViewActiveIndex = 0;
    this.getNdfOperations();
  }

  handleFilter(filter: NdfOperationFilterRequest) {
    const {
      internationalCurrencyId,
      financialInstitutionName,
      financialInstitutionId,
      valueMEFrom,
      valueMETo,
      dueAtFrom,
      dueAtTo,
      operationDateFrom,
      operationDateTo,
      daysCountFrom,
      daysCountTo,
      notionalAmountFrom,
      notionalAmountTo,
      forwardRateFrom,
      forwardRateTo,
      capValueFrom,
      capValueTo,
      referenceExchangeRateFrom,
      referenceExchangeRateTo,
      remarks,
      operationStatus,
      financialStatus,
      composition,
      operationStrategyTypes,
      withoutInstallments,
    } = filter;

    this.ndfOperationFilter = {
      ...this.ndfOperationFilter,
      internationalCurrencyId,
      financialInstitutionName,
      valueMEFrom,
      valueMETo,
      dueAtFrom: (dueAtFrom || '').split('T')[0],
      dueAtTo: (dueAtTo || '').split('T')[0],
      operationDateFrom: (operationDateFrom || '').split('T')[0],
      operationDateTo: (operationDateTo || '').split('T')[0],
      daysCountFrom,
      daysCountTo,
      notionalAmountFrom,
      notionalAmountTo,
      forwardRateFrom,
      forwardRateTo,
      capValueFrom,
      capValueTo,
      referenceExchangeRateFrom,
      referenceExchangeRateTo,
      remarks,
      operationStatus,
      financialStatus,
      composition,
      financialInstitutionId,
      operationStrategyTypes: operationStrategyTypes || [],
    };

    // Tratar withoutInstallments separadamente: se undefined, remover; se definido, adicionar
    if (withoutInstallments !== undefined) {
      this.ndfOperationFilter.withoutInstallments = withoutInstallments;
    } else {
      delete this.ndfOperationFilter.withoutInstallments;
    }

    // Remover campos vazios
    Object.keys(this.ndfOperationFilter).forEach(key => {
      const value = this.ndfOperationFilter[key as keyof NdfOperationsFilteredSummaryParams];
      if (
        value === '' ||
        value === null ||
        value === undefined ||
        (typeof value === 'boolean' && value === false && key !== 'withoutInstallments') || // Não remove withoutInstallments quando false
        (Array.isArray(value) && value.length === 0 && key !== 'operationStrategyTypes') // Não remove operationStrategyTypes mesmo se vazio
      ) {
        delete this.ndfOperationFilter[key as keyof NdfOperationsFilteredSummaryParams];
      }
    });

    // Atualizar filtros atuais
    this.currentFilters = filter;

    this.handleFilterActive(false);

    // Define o tabViewActiveIndex com base no operationStatus
    if (operationStatus) {
      switch (operationStatus) {
        case this.operationStatus.Simulated:
          this.tabViewActiveIndex = 1;
          break;
        case this.operationStatus.Quoted:
          this.tabViewActiveIndex = 2;
          break;
        case this.operationStatus.Hired:
          this.tabViewActiveIndex = 3;
          break;
        default:
          this.tabViewActiveIndex = 0;
      }
    } else {
      this.tabViewActiveIndex = 0;
    }

    this.updateUrlWithFilters();
    this.getNdfOperations();
  }

  // Métodos para o novo padrão de filtros ativos
  onActiveFiltersChange(configs: ActiveFilterConfig[]): void {
    this.activeFilterConfigs = configs;
  }

  updateUrlWithFilters(): void {
    const queryParams: Record<string, string | number | boolean | string[] | null> = {};

    if (this.currentFilters) {
      Object.keys(this.currentFilters).forEach(key => {
        const value = this.currentFilters![key as keyof NdfOperationFilterRequest];
        if (
          value !== null &&
          value !== undefined &&
          value !== '' &&
          !(Array.isArray(value) && value.length === 0)
        ) {
          if (key === 'withoutInstallments') {
            // Converter boolean para string na URL
            queryParams[key] = String(value);
          } else if (Array.isArray(value)) {
            queryParams[key] = value;
          } else {
            queryParams[key] = value;
          }
        }
      });
    }

    // Adicionar o filtro de pesquisa se existir
    if (this.search && this.search.trim() !== '') {
      queryParams['filter'] = this.search;
    }

    Object.assign(queryParams, this.companySelection.buildQueryParam(this.selectedCompanyIds));

    // Se não há parâmetros, limpar a URL
    const hasParams = Object.keys(queryParams).length > 0;

    this.router.navigate([], {
      relativeTo: this.route,
      queryParams: hasParams ? queryParams : {},
      queryParamsHandling: 'replace' as QueryParamsHandling,
    });
  }

  onRemoveFilter(propertyName: string): void {
    if (propertyName === 'filter') {
      // Remover filtro de pesquisa de texto
      this.search = '';
      this.ndfOperationFilter = {
        ...this.ndfOperationFilter,
        filter: '',
      };
      delete this.ndfOperationFilter.filter;

      // Remover dos filtros ativos
      this.activeFilterConfigs = this.activeFilterConfigs.filter(
        config => config.propertyName !== 'filter'
      );

      this.resetPagination();
      this.getNdfOperations();
      this.updateUrlWithFilters();
      this.changeDetectorRef.detectChanges();
      return;
    }

    if (this.currentFilters) {
      const newFilters = { ...this.currentFilters };

      // Handle range filters by removing both min and max
      if (propertyName === 'notionalAmountFrom') {
        delete newFilters.notionalAmountFrom;
        delete newFilters.notionalAmountTo;
        // Also remove from ndfOperationFilter
        delete this.ndfOperationFilter.notionalAmountFrom;
        delete this.ndfOperationFilter.notionalAmountTo;
      } else if (propertyName === 'forwardRateFrom') {
        delete newFilters.forwardRateFrom;
        delete newFilters.forwardRateTo;
        // Also remove from ndfOperationFilter
        delete this.ndfOperationFilter.forwardRateFrom;
        delete this.ndfOperationFilter.forwardRateTo;
      } else if (propertyName === 'capValueFrom') {
        delete newFilters.capValueFrom;
        delete newFilters.capValueTo;
        // Also remove from ndfOperationFilter
        delete this.ndfOperationFilter.capValueFrom;
        delete this.ndfOperationFilter.capValueTo;
      } else if (propertyName === 'referenceExchangeRateFrom') {
        delete newFilters.referenceExchangeRateFrom;
        delete newFilters.referenceExchangeRateTo;
        // Also remove from ndfOperationFilter
        delete this.ndfOperationFilter.referenceExchangeRateFrom;
        delete this.ndfOperationFilter.referenceExchangeRateTo;
      } else if (propertyName === 'dueAtFrom') {
        delete newFilters.dueAtFrom;
        delete newFilters.dueAtTo;
        // Also remove from ndfOperationFilter
        delete this.ndfOperationFilter.dueAtFrom;
        delete this.ndfOperationFilter.dueAtTo;
      } else if (propertyName === 'operationDateFrom') {
        delete newFilters.operationDateFrom;
        delete newFilters.operationDateTo;
        // Also remove from ndfOperationFilter
        delete this.ndfOperationFilter.operationDateFrom;
        delete this.ndfOperationFilter.operationDateTo;
      } else if (
        propertyName === 'financialInstitutionName' ||
        propertyName === 'financialInstitutionId'
      ) {
        // Handle financial institution filters - remove both name and id
        delete newFilters.financialInstitutionName;
        delete newFilters.financialInstitutionId;
        delete this.ndfOperationFilter.financialInstitutionName;
        delete this.ndfOperationFilter.financialInstitutionId;
      } else {
        delete newFilters[propertyName as keyof NdfOperationFilterRequest];
        // Also remove from ndfOperationFilter
        delete this.ndfOperationFilter[propertyName as keyof NdfOperationsFilteredSummaryParams];
      }

      // Check if there are any valid filters left
      const hasValidFilters = Object.keys(newFilters).some(key => {
        const value = newFilters[key as keyof NdfOperationFilterRequest];
        return (
          value !== null &&
          value !== undefined &&
          value !== '' &&
          !(Array.isArray(value) && value.length === 0)
        );
      });

      this.currentFilters = hasValidFilters ? newFilters : null;
      this.resetPagination();
      this.getNdfOperations();
      this.updateUrlWithFilters();
      // Force change detection to update child component
      this.changeDetectorRef.detectChanges();
    }
  }

  onClearAllFilters(): void {
    this.currentFilters = null;
    this.activeFilterConfigs = [];
    this.ndfOperationFilter = {};
    this.search = ''; // Limpar também o search
    this.resetPagination();
    this.getNdfOperations();
    this.updateUrlWithFilters();
    // Force change detection to update child component
    this.changeDetectorRef.detectChanges();
  }

  // Métodos para alternância de visualização
  toggleViewMode(): void {
    this.viewMode = this.viewMode === 'cards' ? 'table' : 'cards';
  }

  setViewMode(mode: 'cards' | 'table'): void {
    this.viewMode = mode;
  }

  // Método para traduzir o status da operação
  getOperationStatusLabel(operationStatus: string): string {
    switch (operationStatus) {
      case 'Simulated':
        return this.t(this.tk.Management.NdfSimulated);
      case 'Quoted':
        return this.t(this.tk.Management.NdfQuoted);
      case 'Hired':
        return this.t(this.tk.Management.NdfHired);
      case 'Reversed':
        return this.t(this.tk.Management.NdfSettledReversed);
      default:
        return operationStatus;
    }
  }

  // Método para traduzir o status financeiro
  getFinancialStatusLabel(financialStatus: string): string {
    switch (financialStatus) {
      case 'Open':
        return this.t(this.tk.Management.NdfOpen);
      case 'Settled':
        return this.t(this.tk.Management.NdfSettled);
      case 'Reversed':
        return this.t(this.tk.Management.NdfSettledReversed);
      default:
        return financialStatus;
    }
  }

  // Método para traduzir o tipo de operação
  getOperationTypeLabel(operationType: string): string {
    switch (operationType) {
      case 'Import':
        return this.t(this.tk.Management.NdfImport);
      case 'Export':
        return this.t(this.tk.Management.NdfExport);
      case 'Loan4131':
        return this.t(this.tk.Management.NdfLoan);
      case 'Indicative':
        return this.t(this.tk.Management.Indicative);
      default:
        return operationType;
    }
  }

  /**
   * Helper para traduzir chaves usadas no mixin
   */
  private translateKey(key: string): string {
    const parts = key.split('.');
    if (parts.length === 2) {
      const [namespace, prop] = parts;
      const tkNamespace = this.tk[namespace as keyof typeof this.tk];
      if (tkNamespace && typeof tkNamespace === 'object') {
        const value = (tkNamespace as Record<string, string>)[prop];
        return this.t(value);
      }
    }
    return key;
  }

  // Método para retornar ações do menu da tabela (usa o mixin)
  getMenuActions(item: NdfOperationFilteredSummary): MenuItem[] {
    return this.actionsMixin.getMenuActions(item);
  }

  // Handler centralizado para ações vindas do mixin
  handleActionFromMixin(action: OperationAction): void {
    const { action: actionType, item, subAction } = action;

    switch (actionType) {
      case 'edit':
        this.handleCardOperationSummaryAction({ action: 'edit', item });
        break;
      case 'attachments':
        this.handleAttachmentsAction(item);
        break;
      case 'mtm':
        this.openMtmForOperation(item);
        break;
      case 'toQuote':
        this.handleCardOperationSummaryAction({ action: 'toQuote', item });
        break;
      case 'toHire':
        this.handleCardOperationSummaryAction({ action: 'toHire', item });
        break;
      case 'toSimulated':
        this.handleCardOperationSummaryAction({ action: 'toSimulated', item });
        break;
      case 'revert-liquidation-to-open':
        this.handleCardOperationSummaryAction({ action: 'revert-liquidation-to-open', item });
        break;
      case 'remove':
        this.handleCardOperationSummaryAction({ action: 'remove', item });
        break;
      case 'toSale':
        this.handleCardOperationSummaryAction({ action: 'toSale', item, subAction });
        break;
      case 'create-committee':
        this.openCreateCommitteeModal(item);
        break;
    }
  }

  // Handler para ações da tabela (compatibilidade)
  handleTableAction({ action, item }: TableAction<NdfOperationFilteredSummary>): void {
    // Conversão de tipo para compatibilidade com CardOperationSummaryAction
    const validAction = action as CardOperationSummaryAction['action'];
    this.handleCardOperationSummaryAction({ action: validAction, item });
  }

  // Método para ordenação da tabela
  handleTableSort({ field, direction }: SortEvent): void {
    const columnDef = this.columnDefinitions.find(col => col.header === field);
    const sortField = columnDef?.sortField || field;

    // Atualizar os parâmetros de paginação com ordenação
    this.paginationRequest = {
      ...this.paginationRequest,
      SortBy: sortField || '',
      SortDirection: direction || '',
    };

    this.getNdfOperations();
  }

  // Método para paginação da tabela
  handleTablePagination(pagination: PaginatorState): void {
    this.handlePagination(pagination);
  }

  override ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
    this.simulateSaleOperationSubject.unsubscribe();
    this.actionsMixin?.destroy();
  }

  getOperationStrategyTypeLabel(operationStrategyType: string | undefined): string {
    switch (operationStrategyType) {
      case 'NDFVanilla':
        return 'NDF Vanilla';
      case 'NDFCAP':
        return 'NDF CAP';
      case 'Callspread':
        return 'Call Spread';
      case 'CallVanilla':
        return 'Call Vanilla';
      case 'SWAP':
        return 'SWAP';
      case 'SwapWithCap':
        return 'SWAP com Cap';
      default:
        return '';
    }
  }

  // Método para verificar se deve mostrar opção de liquidação integral
  shouldShowFullLiquidation(operation: NdfOperationFilteredSummary): boolean {
    const operationDate = operation?.operationDate || '';
    const dueAt = operation?.dueAt || '';
    const internationalCurrencyId = operation?.internationalCurrencyId;

    if (!operationDate || !dueAt) {
      return false;
    }

    const nCurrentDate = new Date();
    const nOperationDate = new Date(operationDate);
    const nDueAt = new Date(dueAt);

    // Condição original
    if (nDueAt >= nOperationDate && nDueAt <= nCurrentDate) {
      return true;
    }

    if (this.ptaxQuotesDates) {
      // Nova condição: (dueAt - 1 dia) == data de hoje (sem considerar hora)
      const today = new Date();
      today.setHours(0, 0, 0, 0); // Zerar componentes de tempo

      const dueAtMinusOneDay = new Date(nDueAt);
      dueAtMinusOneDay.setDate(dueAtMinusOneDay.getDate() - 1);
      dueAtMinusOneDay.setHours(0, 0, 0, 0); // Zerar componentes de tempo

      const isDueAtMinusOneDayToday = dueAtMinusOneDay.getTime() === today.getTime();

      // Verificar se ptaxQuotesDates contém um item com o internationalCurrencyId dado
      const hasMatchingCurrencyId =
        this.ptaxQuotesDates &&
        this.ptaxQuotesDates.some(item => item.internationalCurrencyId === internationalCurrencyId);

      return isDueAtMinusOneDayToday && hasMatchingCurrencyId ? true : false;
    }

    return false;
  }

  handleAttachmentsAction(operation: NdfOperationFilteredSummary | null): void {
    if (!operation?.id) return;

    this.attachmentsModalRef?.open(operation.id);
  }

  onAttachmentUploaded(): void {
    // Refresh data or perform any necessary action after attachment upload
    this.toast.showToastSuccess('Anexos atualizados com sucesso');
  }

  // Métodos para abrir modais de detalhes
  openComexDetailsModal(operationId: string): void {
    if (this.comexDetailsModalRef) {
      this.comexDetailsModalRef.open(operationId);
    }
  }

  openLoanDetailsModal(operationId: string): void {
    if (this.loanDetailsModalRef) {
      this.loanDetailsModalRef.open(operationId);
    }
  }

  onComexDetailsClosed(): void {
    // Callback quando a modal de detalhes COMEX é fechada
    console.log('Modal de detalhes COMEX fechada');
  }

  onLoanDetailsClosed(): void {
    // Callback quando a modal de detalhes de Loan é fechada
    console.log('Modal de detalhes de Loan fechada');
  }

  openMtmForOperation(item: NdfOperationFilteredSummary): void {
    if (!item.id) return;
    this.mtmDailyModal.open(item.id);
  }

  /**
   * Gera o texto do tooltip com todas as parcelas vinculadas, ordenadas por vencimento
   * Flat Fee sempre aparece primeiro quando existe
   */
  getInstallmentsTooltip(installments: InstallmentSummary[] | undefined): string {
    if (!installments || installments.length === 0) {
      return '';
    }

    // Ordenar parcelas: Flat Fee primeiro, depois por data de vencimento
    const sortedInstallments = this.sortInstallments(installments);

    // Criar uma lista formatada com HTML para melhor legibilidade
    let tooltipContent =
      '<div style="text-align: left; font-size: 13px; min-width: 280px; max-width: 400px; padding: 4px;">';

    if (sortedInstallments.length === 1) {
      tooltipContent +=
        '<div style="font-weight: bold; margin-bottom: 6px; border-bottom: 1px solid #ddd; padding-bottom: 4px;">';
      tooltipContent += 'Parcela Vinculada:</div>';
    } else {
      tooltipContent +=
        '<div style="font-weight: bold; margin-bottom: 6px; border-bottom: 1px solid #ddd; padding-bottom: 4px;">';
      tooltipContent += `Parcelas Vinculadas (${sortedInstallments.length}):</div>`;
    }

    sortedInstallments.forEach(installment => {
      tooltipContent += '<div style="padding: 3px 0; line-height: 1.6;">';
      tooltipContent += this.formatInstallmentText(installment);
      tooltipContent += '</div>';
    });

    tooltipContent += '</div>';
    return tooltipContent;
  }

  /**
   * Ordena as parcelas: Flat Fee primeiro, depois por data de vencimento (mais antiga primeiro)
   */
  private sortInstallments(installments: InstallmentSummary[]): InstallmentSummary[] {
    return [...installments].sort((a, b) => {
      // Flat Fee sempre vem primeiro
      if (a.isFlatFee && !b.isFlatFee) return -1;
      if (!a.isFlatFee && b.isFlatFee) return 1;

      // Se ambas são Flat Fee ou ambas não são, ordenar por data de vencimento
      const dateA = new Date(a.dueAt).getTime();
      const dateB = new Date(b.dueAt).getTime();

      return dateA - dateB; // Ordem crescente (mais antiga primeiro)
    });
  }

  /**
   * Formata o texto de uma parcela considerando se é flat fee ou não
   */
  private formatInstallmentText(installment: InstallmentSummary): string {
    const prefix = installment.isFlatFee ? 'Flat Fee' : `Parcela ${installment.installmentNumber}`;
    return `${prefix} | Vencimento: ${this.formatDateForTooltip(installment.dueAt)}`;
  }

  /**
   * Formata a data para exibição no tooltip usando o mesmo formato do pipe vaultDate
   */
  private formatDateForTooltip(date: string): string {
    if (!date) return '-';
    // Usar o DateFormatterService para manter consistência com o pipe vaultDate
    return this.dateFormatter.formatDateInstant(date, true);
  }

  private openCreateCommitteeModal(item: NdfOperationFilteredSummary): void {
    if (!item?.id) return;

    this.utilsService.setIsLoading(true);
    this.getNdfOperationDetailSubscription(item.id).subscribe({
      next: operation => {
        this.utilsService.setIsLoading(false);
        const prefill = this.buildCommitteePrefill(item, operation);
        this.createCommitteeModalRef?.open(prefill);
      },
      error: () => {
        this.utilsService.setIsLoading(false);
        const prefill = this.buildCommitteePrefill(item, null);
        this.createCommitteeModalRef?.open(prefill);
      },
    });
  }

  private buildCommitteePrefill(
    item: NdfOperationFilteredSummary,
    operation: NdfDemandsCalculationsOperation | null
  ): CommitteePrefillOptions {
    const firstInstallment = item.installments?.[0];
    const companyId =
      this.resolveCompanyId(item, operation) ||
      (this.selectedCompanyIds.length === 1 ? this.selectedCompanyIds[0] : '');
    const companyName = (item.demandSummary?.company || item.company || '').trim();
    const derivativeInstallments = operation?.derivativeOperationInstallments ?? [];

    return {
      fromDerivativeList: true,
      companyId: companyId || undefined,
      companyName: companyName || undefined,
      derivativeOperationId: item.id || undefined,
      derivativeSequentialId: item.sequentialId || undefined,
      loanOperationId: item.demandSummary?.loanOperation?.id || undefined,
      loanOperationContractId: item.demandSummary?.loanOperation?.sequentialId || undefined,
      loanOperationInstallmentId:
        firstInstallment?.id || derivativeInstallments[0]?.loanOperationInstallmentId || undefined,
      loanOperationInstallmentNumber: firstInstallment?.installmentNumber ?? null,
    };
  }

  private resolveCompanyId(
    item: NdfOperationFilteredSummary,
    operation: NdfDemandsCalculationsOperation | null
  ): string {
    const operationAsAny = operation as unknown as Record<string, unknown> | null;
    const itemAsAny = item as unknown as Record<string, unknown>;

    const demand =
      (operationAsAny?.['derivativeOperationDemand'] as Record<string, unknown> | undefined) ??
      (operationAsAny?.['ndfDerivateOperationDemand'] as Record<string, unknown> | undefined) ??
      undefined;

    return (
      (demand?.['companyId'] as string | undefined) ||
      (itemAsAny['companyId'] as string | undefined) ||
      ''
    );
  }
}
